import { EventPageType } from '@graphql/generated';
import { MigratedCustomPageSlug } from '@shared/constants/customPageMigration';

const MIGRATED_CUSTOM_PAGE_SLUGS_LIST: ReadonlyArray<MigratedCustomPageSlug> = ['story', 'faq', 'vip', 'welcome', 'schedule'];
export const MIGRATED_CUSTOM_PAGE_SLUGS: ReadonlySet<MigratedCustomPageSlug> = new Set(MIGRATED_CUSTOM_PAGE_SLUGS_LIST);

// Mapping from migrated page slugs to their corresponding legacy EventPageType.
const SLUG_TO_LEGACY_PAGE_TYPE: Readonly<Partial<Record<MigratedCustomPageSlug, EventPageType>>> = {
  story: EventPageType.story,
  faq: EventPageType.faq,
  vip: EventPageType.vip,
  welcome: EventPageType.welcome,
  schedule: EventPageType.schedule,
};

export const getLegacyPageType = (slug: MigratedCustomPageSlug): EventPageType | undefined => SLUG_TO_LEGACY_PAGE_TYPE[slug];

// Mapping from EventPageType to canonical slug for migrated pages.
// e.g., EventPageType.vip → 'vip' (canonical even if the pageSlug differs).
const PAGE_TYPE_TO_SLUG: Readonly<Partial<Record<EventPageType, MigratedCustomPageSlug>>> = {
  [EventPageType.story]: 'story',
  [EventPageType.faq]: 'faq',
  [EventPageType.vip]: 'vip',
  [EventPageType.welcome]: 'welcome',
  [EventPageType.schedule]: 'schedule',
};

export const getMigratedPageSlug = (pageType: EventPageType): MigratedCustomPageSlug | undefined => PAGE_TYPE_TO_SLUG[pageType];

type PageWithTypeAndSlug = Readonly<{
  pageSlug: string;
  type?: EventPageType | null;
}>;

// These custom pages always use native routing/entry even though they're EventPageType.custom.
const ALWAYS_NATIVE_PAGE_SLUGS: ReadonlySet<string> = new Set(['accommodations']);

/**
 * Determines if a page should use the native/dictionary entry (with correct icon/path)
 * instead of the generic custom page handler.
 */
export const shouldUseNativePageEntry = (page: PageWithTypeAndSlug, isMigratedCustomPageEnabled: (slug: string) => boolean): boolean => {
  // Always use native entry for specific slugs (e.g., accommodations)
  if (ALWAYS_NATIVE_PAGE_SLUGS.has(page.pageSlug)) return true;

  // Use native entry for migrated custom pages only when that page's event flag is enabled.
  if (page.type === EventPageType.custom && isMigratedCustomPageEnabled(page.pageSlug)) return true;

  // Use native entry for non-custom pages
  return page.type !== EventPageType.custom;
};
